<template lang="pug">
	.voice-creator--detail
		.flex-col.space-large.padding(v-if="project && initialized")
			.flex-col(:class="isLoadingCssClass")
				.flex-row.space-medium.align-center.justify
					.flex-row.space-medium.align-center
						.flex-row.align-center.space.font-big.upper
							a(@click="$router.push({ path: '/voice-creator' })") {{ $tCaption('Projects') }}
							span /
							span {{ project.Name }}
						.flex-row.space.align-center
							DxButton(v-if="!isEditingProject", icon="edit", @click="isEditingProject = true")
							DxButton(v-if="isEditingProject", icon="check", @click="saveProject", type="success", :disabled="!!isLoading")
							DxButton(v-if="isEditingProject", icon="close", @click="isEditingProject = false")
							Loader(v-if="state['save'] || state['load']")
					.flex-row.space.align-center
						DxButton(v-if="isEditingProject", icon="trash", @click="deleteProject", type="danger", :text="$tCaption('Delete', '@actions')", :disabled="!!isLoading")
				template(v-if="isEditingProject")
					.flex-col.space-medium
						.flex-col
							.font-small.upper.grey {{ $tCaption('Name') }}
							DxTextBox(:value.sync="project.Name", stylingMode="filled")
						.flex-col
							.font-small.upper.grey {{ $tCaption('Description') }}
							DxTextArea(:value.sync="project.Description", :height="80", stylingMode="filled")
				.flex-col.space-medium(v-else)
					.span.grey {{ project.Description }}
					.flex-row.justify
						.flex-col.center
							.flex-row.align-center.space.header-stepper
								.step.flex-row.space.align-center(v-for="(step, idx) in steps", :class="{ active: idx === activeStepIdx, passed: idx < activeStepIdx || !allowEdit }", @click="onStepClick(idx)")
									fas.grey(v-if="idx > 0", icon="right-long")
									.step-name {{step.name}}
							.stepper-line
								.stepper-value(:style="{ '--percent': `${ Math.min(100, 100 * (activeStepIdx + 1) / steps.length) }%` }")
						.flex-row.space.align-center 
							DxButton(v-if="activeStepIdx < steps.length - 1 && allowEdit", :text="$tCaption('Next', '@actions')", @click="nextStep", icon="fas fa-chevron-right", type="default",
								:disabled="activeStep.canNext && !activeStep.canNext()")
					.flex-col.space-large
						.block-area.flex-col.space(v-if="activeStepIdx === 0", :class="getDisabled(state['consent'])")
							.flex-row.space.align-center
								.font-big.upper {{ $tCaption('Consent') }}
								Loader(v-if="state['consent']", text="")
							template(v-if="!state['consent']")
								.grey {{ $tCaption('Before you can train a neural voice, you must submit a recording of the voice talent\'s consent statement.') }}
								AudioRecorder(:link="consent && consent.AudioUrl", @save="onConsentSave", @show="loadConsentText", :recordText="!consent ? $tCaption('Record consent') : $tCaption('Re-take')",
									:readonly="!allowEdit")
									.flex-col.space-medium(slot="text")
										Loader(v-if="state['consent-text']", :text="$tCaption('Preparing consent text')")
										template(v-else)
											.flex-row.space-medium
												.flex-col.space.flex-1
													.font-small.upper.grey {{ $tCaption('First and Last name') }}
													DxTextBox(:value.sync="newConsent.name", :disabled="!!isLoading", stylingMode="filled", valueChangeEvent="input")
												.flex-col.space
													.font-small.upper.grey {{ $tCaption('Locale') }}
													DxSelectBox(:value="newConsent.locale.code", :disabled="!!isLoading", :dataSource="locales", displayExpr="displayName",valueExpr="locale",
														:searchEnabled="true", @valueChanged="onLocaleChange", stylingMode="filled")
											.flex-col.space
												.font-small.upper.grey {{ $tCaption('Description') }}
												DxTextArea(:value.sync="newConsent.description", :disabled="!!isLoading", stylingMode="filled", :height="60")
											.flex-col.space
												.font-big {{ consentTextFormatted }}
						.block-area.flex-col.space-medium(v-if="activeStepIdx === 1 && consent", :class="getDisabled(!consent || state['trainingSet'])")
							.flex-col.space
								.flex-row.space.align-center
									.font-big.upper {{ $tCaption('Training set') }}
									Loader(v-if="state['trainingSet']", text="")
								template(v-if="!state['trainingSet']")
									.grey {{ $tCaption('The Speech service uses this data to create a unique voice tuned to match the voice in the recordings. 20 utterances is enough for Lite voice. ') }}
									.flex-row.space.align-center(v-if="!trainingSet")
										DxButton(:text="$tCaption('Create training set')", @click="showNewTrainingSet")
							template(v-if="trainingSet")
								Loader(v-if="state['phrases']", :text="$tCaption('Loading utterances')")
								template(v-else)
									.flex-row.space-medium.align-center.justify
										.upper.grey {{ $tCaption('Utterances') }} ({{ recordedPhraseCount }}/{{ trainingPhrases.length }}):
										Pager(:count="pageCount", v-model="pageIndex")
									.phrases.flex-col.space
										.phrase.flex-row.space(v-for="(ph, idx) in visiblePhrases", @click="showPhrase(ph, idx)") 
											.flex-row.space.align-center
												fas(:icon="ph.__isLoading ? 'spinner' : (ph.fileName ? 'square-check' : 'far fa-square')", :spin="ph.__isLoading")
												span {{ (pageIndex * pageCount) + idx + 1 }}.
											span {{ ph.text }}
						.block-area.flex-col.space-medium(v-if="activeStepIdx === 2")
							.flex-col.space-medium
								.flex-row.space.align-center
									.font-big.upper {{ $tCaption('Publishing') }}
								.flex-col.space-medium
									.flex-col
										.font-tiny.upper.grey {{ $tCaption('Model name') }}
										.flex-row.align-center.space(v-if="!voiceModel")
											DxTextBox.flex-basis-500(:value.sync="newModel.name", stylingMode="filled", valueChangeEvent="input", :placeholder="modelDefaults.name")
											.grey(v-if="newModel.name") ({{newModel.name || ''}}Neural)
										span(v-else) {{ voiceModel.Name }}
									.flex-col
										.font-tiny.upper.grey {{ $tCaption('Model description') }}
										.flex-row
											DxTextArea.flex-basis-500(v-if="!voiceModel", :value.sync="newModel.description", stylingMode="filled", :disabled="!!voiceModel", valueChangeEvent="input", :placeholder="modelDefaults.description")
											span(v-else) {{ voiceModel.Description }}
								.grey {{ $tCaption('This will upload all project assets to Azure: the project itself, consents and training data sets. Then, dataset training will be initiated on Azure. This process can take a lot of time.') }}
								.flex-row.space.align-center(v-if="project.ProjectPublishingStatus === 0")
									DxButton(:text="$tCaption('Publish')", @click="doPublish")
								.font-big.flex-row.space.align-center(v-else, :class="projectStatusClassName")
									fas(:icon="project.ProjectPublishingStatus === 1 ? 'spinner' : (project.ProjectPublishingStatus === 2 ? 'check' : 'times')", :spin="project.ProjectPublishingStatus === 1")
									span {{ projectPublishingStatusName }}
		Loader(v-if="isLoading", :fullscreen="true")
		AudioRecorder(ref="phraseRecorder", @save="onPhraseSave", :hideUI="true", :readonly="!allowEdit", :showNext="true", @next="nextPhrase")
			.font-big(slot="text") {{ activePhrase && activePhrase.text }}
			.voice-creator--detail(v-if="playingLink", slot="actions-end")
				.phrase-audio
					audio(:src="playingLink", controls)
</template>

<script>
	import DxButton from 'devextreme-vue/button';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxTextArea from 'devextreme-vue/text-area';
	import Loader from 'app-core/Loader.vue';
	import Popup from 'app-core/Popup.vue';
	import AudioRecorder from './AudioRecorder.vue';
	import DxSelectBox from 'devextreme-vue/select-box';
	import DxScrollView from 'devextreme-vue/scroll-view';
	import Pager from 'app-core/Pager.vue';
	import { VOICE_CREATOR_PUBLISHING_STATUS } from 'app-core/constants';

	export default {
		translationCategory: 'voiceCreator.Detail',
		name: 'voice-creator-detail',
		components: {
			DxButton, Loader, DxTextBox, DxTextArea, Popup, AudioRecorder, DxSelectBox, DxScrollView, Pager
		},
		props: ['id'],
		data() {
			return {
				isLoading: 0,
				project: null,
				isEditingProject: false,
				consent: null,
				state: {},
				newConsent: {
					name: null,
					description: null,
					companyName: null,
					locale: {
						code: window.navigator?.language ||'en-US'
					}
				},
				consentText: null,
				trainingSet: null,
				trainingPhrases: [],
				newTrainingSet: {
					VoiceKind: 0
				},
				voiceKinds: [this.$tCaption('Male'), this.$tCaption('Female')].map((f, i) => ({ id: i, name: f })),
				pageIndex: 0,
				steps: [
					{ name: this.$tCaption('Consent') },
					{ name: this.$tCaption('Training set'), canNext: () => this.recordedPhraseCount >= 20 || (__DEBUG__ && this.$auth.isLocalhost()) },
					{ name: this.$tCaption('Publishing'), onActivate: this.onPublishingActivate }
				],
				activeStepIdx: 0,
				activePhrase: null,
				initialized: false,
				playingLink: null,
				publishingStatus: VOICE_CREATOR_PUBLISHING_STATUS.toLocale('name', this),
				voiceModel: null,
				newModel: {
					description: '',
					name: ''
				}
			};
		},
		computed: {
			isLoadingCssClass() {
				return { 'disabled-controls': this.isLoading };
			},
			locales() {
				return this.$Data.langmapItems();
			},
			pageCount() {
				return 20;
			},
			phrasesPageCount() {
				return Math.ceil(this.trainingPhrases.length / this.pageCount);
			},
			visiblePhrases() {
				return this.trainingPhrases.slice(this.pageIndex * this.pageCount, (this.pageIndex + 1) * this.pageCount)
			},
			activeStep() {
				return this.steps[this.activeStepIdx];
			},
			recordedPhraseCount() {
				return this.trainingPhrases.filter(f => f.fileName).length;
			},
			projectPublishingStatusName() {
				return this.publishingStatus[this.project.ProjectPublishingStatus]?.name;
			},
			projectStatusClassName() {
				return ['', 'warning-colored', 'success-colored', 'error-colored'][this.project.ProjectPublishingStatus];
			},
			allowEdit() {
				return (this.project?.ProjectPublishingStatus || 0) === 0;
			},
			consentTextFormatted() {
				return (this.consentText || '')
					.replace(/\[(.*?)\]/, this.newConsent.name)
					.replace(/\[(.*?)\]/, this.$tCaption('Adthos', '@actions'));
			},
			modelDefaults() {
				return {
					name: `${this.consent?.Locale?.Code}_${this.$Data.formatStringAsKey(this.project?.Name)}`,
					description: `${this.project?.Name}, ${this.consent?.Locale?.Code}, ${this.voiceKinds[this.project.Gender]?.name}`
				}
			}
		},
		watch: {
			id() {
				this.loadVoice();
			},
			'newConsent.locale.code'() {
				this.loadConsentText();
			},
			activePhrase(v) {
				this.playingLink = null;
				if (v?.fileName) {
					this.$azure.getBlobDirectLink(`${v.fileName}.wav`, this.trainingSet.ContainerName).then(r => this.playingLink = r);
				}
			}
		},
		mounted() {
			if (__DEBUG__)
				window.voiceCreatorDetail = this;
			
			this.loadVoice();
		},
		destroyed() {
			
		},
		methods: {
			async nextPhrase() {
				let wav = await this.$refs.phraseRecorder.getFinalAudio();
				if (wav) {
					this.$notifySuccess('wav');
					this.onPhraseSave(wav);
				}

				this.$refs.phraseRecorder.clearResults();
				this.$refs.phraseRecorder.reinit();

				let cIdx = this.trainingPhrases.findIndex(f => f === this.activePhrase);
				if (cIdx > -1 && this.trainingPhrases[cIdx + 1]) {
					this.activePhrase = this.trainingPhrases[cIdx + 1];
				}

				if (wav) {
					this.$nextTick(() => {
						if (this.$refs.phraseRecorder) {
							this.$refs.phraseRecorder.startRecording();
						}
					});
				}
			},
			nextStep() {
				this.activeStepIdx += 1;

				this.$nextTick(() => {
					if (this.activeStep.onActivate) {
						this.activeStep.onActivate();
					}
				})
			},
			onPublishingActivate() {
				this.newModel.name = this.modelDefaults.name;
				this.newModel.description = this.modelDefaults.description;
			},
			onStepClick(idx) {
				if (this.allowEdit) {
					this.activeStepIdx = Math.min(this.activeStepIdx, idx)
				} else {
					this.activeStepIdx = idx;
				}
			},
			doPublish() {
				this.$confirm(void 0, this.$tCaption('Would you like to upload your training data to Azure?')).then(() => {
					this.isLoading++;
					this.$Data.postExternalApi('VoiceProject/Publish', { Oid: this.id })
						.then(() => {
							this.project.ProjectPublishingStatus = 1;
							this.checkProjectUpdateStatus();
							this.createVoiceModel();
						})
						.finally(() => this.isLoading--);
				});
			},
			async createVoiceModel() {
				if (this.voiceModel) return;

				this.voiceModel = (await this.$Data.postExternalApi('VoiceModel/CreateUpdate', {
					name: `${this.newModel.name || this.modelDefaults.name || ''}Neural`,
					description: this.newModel.description || this.modelDefaults.description,
					locale: {
						code: this.consent.Locale?.Code
					},
					project: {
						Oid: this.id
					},
					consent: {
						Oid: this.consent?.Oid
					},
					trainingSet: {
						Oid: this.trainingSet?.Oid
					}
				}))?.Data;
			},
			onPhraseSave(wav) {
				let phrase = this.activePhrase;
				let fileName = phrase.fileName || `file${parseInt(phrase.code) || phrase.code}`;
				this.$set(phrase, 'fileName', fileName);
				this.$set(phrase, '__isLoading', true);
				this.$azure.uploadBlob(`${fileName}.wav`, wav, this.trainingSet?.ContainerName).finally(() => this.$set(phrase, '__isLoading', false));
				this.savePhrasesFile();
			},
			showPhrase(phrase) {
				this.activePhrase = phrase;
				this.$refs.phraseRecorder.show();
			},
			showNewTrainingSet() {
				this.$confirm(void 0, this.$tCaption('Would you like to create new training set?')).then(() => this.initTrainingSet());
			},
			async initTrainingSet() {
				this.loadTrainingPhrases();

				if (!this.trainingSet) {
					this.$set(this.state, 'trainingSet', true);
					this.trainingSet = (await this.$Data.postExternalApi('VoiceTrainingSet/CreateUpdate', {
						Locale: {
							Code: this.consent.Locale?.Code || 'en-US',
						},
						VoiceKind: this.project.Gender || 0,
						Project: {
							Oid: this.id
						}
					}).finally(() => this.$set(this.state, 'trainingSet', false)))?.Data || this.trainingSet;
				}

				if (!this.trainingSet.ContainerName) {
					this.$set(this.state, 'trainingSet', true);
					this.trainingSet.ContainerName = (await this.$Data.postExternalApi('VoiceTrainingSet/CreateContainer', { Oid: this.trainingSet.Oid })
						.finally(() => this.$set(this.state, 'trainingSet', false))
					)?.Data?.containerName;
				}
			},
			async loadPhrasesFile() {
				if (!this.trainingSet?.ContainerName) return;

				let result = await this.$azure.getBlob('phrases.txt', true, void 0, true, this.trainingSet?.ContainerName).catch(() => null);

				if (!result) {
					this.savePhrasesFile();
				} else {
					let files = ((await result.text()) || '').split('\n');
					files.map(f => {
						let ph = f.split('\t')[1];

						let tPhrase = this.trainingPhrases.find(t => t.text === ph);
						if (tPhrase) {
							tPhrase.fileName = f.split('\t')[0];
						}
					});
				}
			},
			async savePhrasesFile() {
				let text = this.trainingPhrases.filter(f => f.fileName).map(f => `${f.fileName}\t${f.text}`).join('\n');
				let blob = new Blob([text]);
				this.$set(this.state, 'save', true);
				this.$azure.uploadBlob('phrases.txt', blob, this.trainingSet?.ContainerName).finally(() => this.$set(this.state, 'save', false));
			},
			async loadTrainingPhrases() {
				if (this.trainingPhrases?.length) return;
				this.pageIndex = 0;
				this.$set(this.state, 'phrases', true);
				this.trainingPhrases = (await this.$Data.getExternalApi('VoiceConsent/GetSamplePhrases', `languageCode=${this.consent.Locale?.Code || 'en-US'}`).finally(() => this.$set(this.state, 'phrases', false)))
					?.split('\n').map(f => ({
						code: f.split('\t')[0],
						text: f.split('\t')[1],
						fileName: null
					}));

				if (this.trainingSet?.ContainerName) {
					this.$set(this.state, 'phrases', true);
					this.loadPhrasesFile().finally(() =>  this.$set(this.state, 'phrases', false));
				}
			},
			async loadTrainingSet() {
				this.$set(this.state, 'trainingSet', true);
				this.trainingSet = ((await this.$Data.getExternalApi('VoiceTrainingSet/List', `page=1&filterCriteria=['Project.Oid', '=', '${this.id}']`).finally(() => this.$set(this.state, 'trainingSet', false)))?.Results || [])[0];
			},
			async loadConsentText() {
				this.$set(this.state, 'consent-text', true);
				this.consentText = await this.$Data.getExternalApi('VoiceConsent/GetConsentPhrase', `languageCode=${this.newConsent.locale.code || 'en-US'}`).finally(() => this.$set(this.state, 'consent-text', false));

				this.newConsent.name = this.$auth.displayUserName();
			},
			onLocaleChange(v) {
				this.newConsent.locale.code = v.value;
			},
			async onConsentSave(wav) {
				if (__DEBUG__)
					console.debug('onConsentSave', { wav });
				this.$set(this.state, 'consent', true);
				try {
					let audio = await this.$azure.uploadBlobWithInfoResponse(`consent_${this.$Data.uuid()}.wav`, wav, 'other');

					let consent = {
						...this.newConsent,
						Oid: this.consent?.Oid,
						project: {
							Oid: this.id
						},
						audio
					}

					this.consent = (await this.$Data.postExternalApi('VoiceConsent/CreateUpdate', consent))?.Data || this.consent;
				} finally {
					this.$set(this.state, 'consent', false);
				}
			},
			getDisabled(v) {
				if (v) {
					return { 'disabled-controls': true };
				}
				return null;
			},
			async loadConsent() {
				this.$set(this.state, 'consent', true);
				this.consent = ((await this.$Data.getExternalApi('VoiceConsent/List', `page=1&filterCriteria=['Project.Oid', '=', '${this.id}']`).finally(() => this.$set(this.state, 'consent', false)))?.Results || [])[0];
			},
			async loadVoice() {
				this.isLoading++;
				this.initialized = false;
				try {
					this.project = await this.$Data.getExternalApi('VoiceProject/Get', `id=${this.id}`);

					await Promise.all([
						this.loadConsent(),
						this.loadTrainingSet(),
						this.loadVoiceModel()
					]);

					if (this.trainingSet) {
						this.loadTrainingPhrases();
					}

					if (this.trainingSet) this.activeStepIdx = 1;
					if (this.project.ProjectPublishingStatus > 0) this.activeStepIdx = 2;

					this.checkProjectUpdateStatus();
				} finally {
					this.isLoading--;
					this.initialized = true;
				}
			},
			async saveProject() {
				this.isLoading++;
				this.$Data.postExternalApi('VoiceProject/CreateUpdate', this.project).finally(() => this.isLoading--);
				this.isEditingProject = false;
			},
			async deleteProject() {
				this.$confirm(void 0, this.$tCaption('Do you want to delete this voice cloning project?')).then(() => {
					this.isLoading++;
					this.$Data.postExternalApi('VoiceProject/Delete', this.project).then(() => this.$router.push({ path: '/voice-creator' })).finally(() => this.isLoading--);
				});
			},
			async checkProjectUpdateStatus() {
				if (!this.project || this._isDestroyed || this.project.ProjectPublishingStatus !== 1) return;

				let res = await this.$Data.getExternalApi('VoiceProject/Get', `id=${this.project.Oid}`);
				this.project.ProjectPublishingStatus = res?.ProjectPublishingStatus;

				if (this.project.ProjectPublishingStatus === 1) {
					setTimeout(this.checkProjectUpdateStatus, (__DEBUG__ ? 10 : 30) * 1000);
				}
			},
			async loadVoiceModel() {
				let existingOid =  ((await this.$Data.getExternalApi('VoiceModel/List', `page=1&filterCriteria=['Project.Oid', '=', '${this.id}']`).finally(() => this.$set(this.state, 'voiceModel', false)))?.Results || [])[0]?.Oid;
				if (existingOid) {
					this.voiceModel = await this.$Data.getExternalApi('VoiceModel/Get', `id=${existingOid}`);
				}
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.voice-creator--detail {
		.block-area {
			border: 1px solid #eaeaea;
			border-left: 4px solid #dedede;
			padding: 10px;
			background: #efefef;
			border-radius: 0px 8px 8px 0px;
		}

		.phrases {
			.phrase {
				font-size: 16px;
				font-weight: 100;
				cursor: pointer;
				padding: 4px;
				border-radius: 4px;
				
				&:hover {
					background: $base-accent;
					color: $base-bg;
				}
			}
		}

		.stepper-line {
			height: 2px;
			width: 100%;
			position: relative;
			background: var(--branding-input-bg);
			border-radius: 2px;

			.stepper-value {
				border-radius: 2px;
				transition: 0.3s;
				position: absolute;
				left: 0px;
				top: 0px;
				height: 100%;
				width: var(--percent);
				background: var(--branding-highlight-color);
			}
		}

		.header-stepper {
			padding: 4px;

			.step {
				opacity: 0.25;
				font-weight: 100;
				transition: 0.3s;
				user-select: none;

				.step-name {
					transition: 0.2s;
				}

				&.passed {
					opacity: 1;
					cursor: pointer;

					.step-name {
						&:hover {
							color: $base-accent;
						}
					}
				}

				&.active {
					opacity: 1;
					color: $base-accent;
					cursor: pointer;
					.step-name {
						font-weight: 500;
					}
				}
			}
		}
	}
</style>
 